/**
 * Firebase Analytics (GA4) — Capacitor Native Integration
 * --------------------------------------------------------
 * Uses @capacitor-firebase/analytics to send events to Google Analytics 4
 * via native Firebase SDK on iOS/Android. Falls back silently on web.
 *
 * Prerequisites:
 *  - google-services.json in frontend/android/app/
 *  - GoogleService-Info.plist in frontend/ios/App/App/
 */

import { Capacitor } from '@capacitor/core';

let FirebaseAnalytics = null;
let _initialized = false;

/**
 * GDPR consent gate.
 *
 * Analytics collection is OFF by default. The user grants consent by
 * accepting the Privacy Policy during onboarding (see OnboardingV2.js
 * step 20 → handleNext, which calls `setAnalyticsConsent(true)`), and
 * can withdraw it by contacting support to have their account erased.
 *
 * Consent state is persisted in localStorage so it survives reloads
 * inside the Capacitor WebView and the browser.
 */
const CONSENT_KEY = 'bh_analytics_consent';

function _hasConsent() {
  try {
    return typeof localStorage !== 'undefined' && localStorage.getItem(CONSENT_KEY) === '1';
  } catch (_) {
    return false;
  }
}

/**
 * Grant or revoke analytics consent. Called from the Privacy Policy
 * acceptance flow. Idempotent.
 */
export async function setAnalyticsConsent(granted) {
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(CONSENT_KEY, granted ? '1' : '0');
    }
  } catch (_) { /* best-effort */ }

  // Reflect the change in the live native plugin if it has been loaded.
  if (FirebaseAnalytics) {
    try {
      await FirebaseAnalytics.setEnabled({ enabled: !!granted });
    } catch (err) {
      console.warn('[GA] setEnabled failed:', err);
    }
  }
}

/**
 * GDPR consent withdrawal — wipes the device-local Firebase Analytics
 * App Instance ID so future events can't be linked to past ones, and
 * disables further collection. Called by UserContext on next login when
 * the server-side `analytics_opt_out` flag is true.
 */
export async function resetAnalyticsData() {
  // Force-disable consent first so any in-flight calls become no-ops.
  await setAnalyticsConsent(false);

  if (!Capacitor.isNativePlatform()) return;

  // The plugin may not have been imported yet (consent gate skipped it).
  // Import it just-in-time so we can call resetAnalyticsData().
  let plugin = FirebaseAnalytics;
  if (!plugin) {
    try {
      const mod = await import('@capacitor-firebase/analytics');
      plugin = mod.FirebaseAnalytics;
    } catch (err) {
      console.warn('[GA] reset: plugin import failed:', err);
      return;
    }
  }

  try {
    await plugin.setEnabled({ enabled: false });
  } catch (_) { /* ignore */ }
  try {
    await plugin.resetAnalyticsData();
  } catch (err) {
    console.warn('[GA] resetAnalyticsData failed:', err);
  }
}

async function _init() {
  if (_initialized) return;
  _initialized = true;

  if (!Capacitor.isNativePlatform()) return;

  // Only load the native plugin if the user has consented. Without consent
  // we deliberately skip plugin import so the SDK never starts collecting.
  if (!_hasConsent()) return;

  try {
    const mod = await import('@capacitor-firebase/analytics');
    FirebaseAnalytics = mod.FirebaseAnalytics;
    await FirebaseAnalytics.setEnabled({ enabled: true });
  } catch (err) {
    console.warn('[GA] Firebase Analytics init failed:', err);
    FirebaseAnalytics = null;
  }
}

/**
 * Log a custom event to Firebase Analytics / GA4.
 * @param {string} name  — event name (max 40 chars, snake_case recommended)
 * @param {Object} params — event parameters (values must be string | number)
 */
export async function logEvent(name, params = {}) {
  if (!_hasConsent()) return;
  await _init();
  if (!FirebaseAnalytics) return;

  try {
    // Firebase Analytics requires param values to be string or number
    const sanitized = {};
    for (const [k, v] of Object.entries(params)) {
      if (v != null) sanitized[k] = typeof v === 'number' ? v : String(v);
    }
    await FirebaseAnalytics.logEvent({ name, params: sanitized });
  } catch (err) {
    console.warn('[GA] logEvent failed:', err);
  }
}

/**
 * Set the current user ID for Firebase Analytics.
 */
export async function setUserId(userId) {
  if (!_hasConsent()) return;
  await _init();
  if (!FirebaseAnalytics) return;

  try {
    await FirebaseAnalytics.setUserId({ userId });
  } catch (err) {
    console.warn('[GA] setUserId failed:', err);
  }
}

/**
 * Set a user property (e.g., subscription_tier, language).
 */
export async function setUserProperty(key, value) {
  if (!_hasConsent()) return;
  await _init();
  if (!FirebaseAnalytics) return;

  try {
    await FirebaseAnalytics.setUserProperty({ key, value: String(value) });
  } catch (err) {
    console.warn('[GA] setUserProperty failed:', err);
  }
}

/**
 * Set the current screen name for automatic screen_view events.
 */
export async function setCurrentScreen(screenName) {
  if (!_hasConsent()) return;
  await _init();
  if (!FirebaseAnalytics) return;

  try {
    await FirebaseAnalytics.setCurrentScreen({ screenName });
  } catch (err) {
    console.warn('[GA] setCurrentScreen failed:', err);
  }
}
